/* Build in libraries */

/* Third party libraries */

/* Components */
import PieChart from "components/UI/Charts/PieChart";
import DoughnutChart from "components/UI/Charts/DoughnutChart";
import MapGoogle from "components/UI/GoogleMap/GoogleReactMap";
import Card from "components/UI/Card";

/* Services */
import TotalHousingPrice from "components/Rover/Widgets/TotalHousingPrice";
import AverageHouseholdIncome from "components/Rover/Widgets/AverageHouseholdIncome";
import ApprovedApplicationsWidget from "components/Rover/Widgets/ApprovedApplicationsWidget";
import { races } from "../BDPAForm/ApplicationInformation/constants";

interface IApplicationDashboardComponentProps {
  data: any;
}

interface IDoughnutChart {
  dataLabels: string[];
  dataSets: { data: number; color: string }[];
}

interface IPieChart {
  label: string[];
  dataSets: { data: number; color: string }[];
}

export const BDPAApprovedDashboard = ({
  data,
}: IApplicationDashboardComponentProps) => {
  const applicationsList = data?.data;
  const widgetsData: {
    totalApplications: number;
    totalHousePrice: number;
    statusSummary: { [key: string]: number };
    totalCityEmployees: IDoughnutChart;
    gender: IDoughnutChart;
    totalHouseholdIncome: number;
    maritalStatus: IPieChart;
    totalRaces: IPieChart;
  } = {
    totalApplications: 0,
    totalHousePrice: 0,
    statusSummary: {},
    totalCityEmployees: {
      dataLabels: ["Yes", "No"],
      dataSets: [
        { data: 0, color: "#D9B64E" },
        { data: 0, color: "#0FCA98" },
      ],
    },
    gender: {
      dataLabels: ["Female", "Male"],
      dataSets: [
        { data: 0, color: "#D9B64E" },
        { data: 0, color: "#0FCA98" },
      ],
    },
    totalHouseholdIncome: 0,
    maritalStatus: {
      label: ["Married", "Unmarried", "Widowed"],
      dataSets: [
        { data: 0, color: "#0FCA98" },
        { data: 0, color: "#D9B64E" },
        { data: 0, color: "#2B6CB0" },
      ],
    },
    totalRaces: {
      label: races,
      dataSets: [
        { data: 0, color: "#D9B64E" },
        { data: 0, color: "#0FCA98" },
        { data: 0, color: "#2B6CB0" },
        { data: 0, color: "#F27141" },
      ],
    },
  };

  applicationsList?.forEach((a: any) => {
    const { applicationData } = a;

    if (a.status === "APPROVED") {
      //Total Application Widget
      widgetsData["totalApplications"] += 1;

      // Total Amount Requested
      widgetsData["totalHousePrice"] += applicationData?.propertyInformation
        ?.housePrice
        ? applicationData?.propertyInformation?.housePrice
        : 0;

      //Status Summary
      widgetsData["statusSummary"][a.status] =
        (widgetsData["statusSummary"][a.status] ?? 0) + 1;

      if (applicationData?.application?.cityEmployee === "yes") {
        widgetsData.totalCityEmployees.dataSets[0].data += 1;
      }
      if (applicationData?.application?.cityEmployee === "no") {
        widgetsData.totalCityEmployees.dataSets[1].data += 1;
      }

      // gender
      if (applicationData?.application?.gender === "Female") {
        widgetsData.gender.dataSets[0].data += 1;
      }
      if (applicationData?.application?.gender === "Male") {
        widgetsData.gender.dataSets[1].data += 1;
      }

      // marital status
      if (applicationData?.application?.maritalStatus === "Un-Married") {
        widgetsData.maritalStatus.dataSets[1].data += 1;
      }
      if (applicationData?.application?.maritalStatus === "Married") {
        widgetsData.maritalStatus.dataSets[0].data += 1;
      }
      if (applicationData?.application?.maritalStatus === "Widow") {
        widgetsData.maritalStatus.dataSets[2].data += 1;
      }

      // Total Household Income
      widgetsData["totalHouseholdIncome"] +=
        applicationData?.householdIncome.reduce((acc: any, curr: any) => {
          // otherIncome: '';
          // pensions: '';
          // publicAssistance: '';
          // relation: 'Sibling';
          // wages: '';
          const wages = curr?.wages !== "" ? Number(curr?.wages) : 0;
          const pensions = curr?.pensions !== "" ? Number(curr?.pensions) : 0;
          const publicAssistance =
            curr?.publicAssistance !== "" ? Number(curr?.publicAssistance) : 0;
          const otherIncome =
            curr?.otherIncome !== "" ? Number(curr?.otherIncome) : 0;
          return (acc += wages + pensions + publicAssistance + otherIncome);
        }, 0);

      // Total Races
      applicationData?.application?.races.forEach((r: any) => {
        if (r === "African-American")
          widgetsData.totalRaces.dataSets[0].data += 1;
        if (r === "Caucasian") widgetsData.totalRaces.dataSets[1].data += 1;
        if (r === "Hispanic") widgetsData.totalRaces.dataSets[2].data += 1;
        if (r === "Other") widgetsData.totalRaces.dataSets[3].data += 1;
      });
    }
  });

  const convertIntoPercentage = (
    arrayOfData: { data: number; color: string }[]
  ) => {
    const totalData = arrayOfData.reduce(
      (acc: number, currentData: { data: number; color: string }) => {
        return (acc += currentData.data);
      },
      0
    );
    const percentage = arrayOfData?.map(
      (d: { data: number; color: string }) => {
        const convertNumberIntoPercentage = (d.data * 100) / totalData;
        const convertFloatToInteger = Math.ceil(convertNumberIntoPercentage);
        return {
          ...d,
          data: convertFloatToInteger ? convertFloatToInteger : 0,
        };
      }
    );

    return percentage;
  };

  return (
    <>
      <>
        <div className="my-4">
          <section className="my-4 grid md:grid-cols-2 xl:grid-cols-4 gap-4">
            <ApprovedApplicationsWidget
              count={widgetsData?.totalApplications ?? 0}
            />
            <TotalHousingPrice count={widgetsData?.totalHousePrice ?? 0} />
            <AverageHouseholdIncome
              count={widgetsData?.totalHouseholdIncome ?? 0}
            />
          </section>

          <section className="grid md:grid-cols-2 xl:grid-cols-2 gap-4 mt-4">
            <Card title="City Employees">
              <div className="p-5 flex flex-col items-center ">
                <div className="w-5/6 lg:w-2/3 ">
                  <DoughnutChart
                    dataLabels={widgetsData?.totalCityEmployees?.dataLabels}
                    datasets={convertIntoPercentage(
                      widgetsData?.totalCityEmployees?.dataSets
                    )}
                    percentage={true}
                  />
                </div>
              </div>
            </Card>
            <Card title="Gender">
              <div className="p-5 flex flex-col items-center ">
                <div className="w-5/6 lg:w-2/3 ">
                  <DoughnutChart
                    dataLabels={widgetsData?.gender?.dataLabels}
                    datasets={convertIntoPercentage(
                      widgetsData?.gender?.dataSets
                    )}
                    percentage={true}
                  />
                </div>
              </div>
            </Card>
          </section>
          <section className="grid md:grid-cols-2 xl:grid-cols-2 gap-4 mt-4">
            <Card title="Marital Status">
              <div className="p-5 flex flex-col items-center ">
                <div className="w-5/6 lg:w-2/3 h-80">
                  <PieChart
                    labels={widgetsData?.maritalStatus?.label}
                    datasets={widgetsData?.maritalStatus?.dataSets}
                    chartTitle=""
                  />
                </div>
              </div>
            </Card>
            <Card title="Race">
              <div className="p-5 flex flex-col items-center ">
                <div className="w-5/6 lg:w-5/5 h-80">
                  <PieChart
                    labels={widgetsData?.totalRaces?.label}
                    datasets={convertIntoPercentage(
                      widgetsData?.totalRaces?.dataSets
                    )}
                    chartTitle=""
                    showValue={true}
                  />
                </div>
              </div>
            </Card>
          </section>
        </div>
      </>
    </>
  );
};
